import { LineItem, Revenue, RevenueComponent, RevenuePeriod, Status } from '@vendasta/sales-orders';
import { Currency as PackageCurrency, Frequency, Package, Pricing } from '@vendasta/marketplace-packages';
import { App, AppPrice, UnitBillingFrequency } from '@galaxy/marketplace-apps';
import { BadgeColor } from '@vendasta/galaxy/badge/src/badge.component';
import { LineItem as OpportunityLineItem } from '@vendasta/sales-opportunities';

export interface Badge {
  text: string;
  color: BadgeColor;
}

export const StatusToBadgeMapping = new Map<Status, Badge>([
  [Status.ACTIVATION_ERRORS, { text: 'FRONTEND.SALES_UI.ACTIVATION_ERRORS', color: 'red' }],
  [Status.APPROVED, { text: 'FRONTEND.SALES_UI.APPROVED', color: 'green' }],
  [Status.DECLINED, { text: 'FRONTEND.SALES_UI.DECLINED', color: 'dark-grey-solid' }],
  [Status.FULFILLED, { text: 'FRONTEND.SALES_UI.ACTIVATED', color: 'blue' }],
  [Status.PROCESSING, { text: 'FRONTEND.SALES_UI.PRODUCT_ACTIVATION_INITIATED', color: 'yellow' }],
  [Status.SUBMITTED, { text: 'FRONTEND.SALES_UI.SUBMITTED_FOR_ADMIN_APPROVAL', color: 'blue' }],
  [Status.DRAFTED, { text: 'FRONTEND.SALES_UI.DRAFTED', color: 'grey' }],
  [Status.ARCHIVED, { text: 'FRONTEND.SALES_UI.ARCHIVED', color: 'blue-solid' }],
  [
    Status.SUBMITTED_FOR_CUSTOMER_APPROVAL,
    { text: 'FRONTEND.SALES_UI.SUBMITTED_FOR_CUSTOMER_APPROVAL', color: 'yellow' },
  ],
  [Status.CANCELLATION_REQUESTED, { text: 'FRONTEND.SALES_UI.CANCELLATION_REQUESTED', color: 'red' }],
  [Status.CANCELLED, { text: 'FRONTEND.SALES_UI.CANCELLED', color: 'blue' }],
  [Status.SCHEDULED_ACTIVATION, { text: 'FRONTEND.SALES_UI.SCHEDULED_ACTIVATION', color: 'yellow' }],
  // No default color defined, defaulting to grey.
  [Status.AWAITING_PAYMENT, { text: 'FRONTEND.SALES_UI.AWAITING_PAYMENT', color: 'grey' }],
  [Status.PROCESSING_PAYMENT, { text: 'FRONTEND.SALES_UI.PROCESSING_PAYMENT', color: 'grey' }],
  [undefined, { text: 'FRONTEND.SALES_UI.SUBMITTED_FOR_ADMIN_APPROVAL', color: 'grey' }],
]);

export function convertPackagePricingToSalesOrderRevenue(pricing: Pricing): Revenue {
  const prices = !!pricing && !!pricing.prices ? pricing.prices : [];
  return new Revenue({
    revenueComponents: prices.map(
      (p) =>
        new RevenueComponent({
          value: p.price,
          period: packageFrequencyToSalesOrderPeriod(p.frequency),
          isStartingRevenue: p.isStartingPrice,
        }),
    ),
  });
}

function packageFrequencyToSalesOrderPeriod(f: Frequency): RevenuePeriod {
  switch (f) {
    case Frequency.DAILY:
      return RevenuePeriod.DAILY;
    case Frequency.MONTHLY:
      return RevenuePeriod.MONTHLY;
    case Frequency.ONCE:
      return RevenuePeriod.ONETIME;
    case Frequency.WEEKLY:
      return RevenuePeriod.WEEKLY;
    case Frequency.YEARLY:
      return RevenuePeriod.YEARLY;
    default:
      return RevenuePeriod.MONTHLY;
  }
}

export function packageCurrencyToCurrencyCode(currency: PackageCurrency): string {
  if (!currency || !PackageCurrency[currency]) {
    return 'USD';
  }
  return PackageCurrency[currency];
}

export function packageToSalesOrderLineItem(pkg: Package): LineItem {
  return new LineItem({
    packageId: pkg.packageId,
    currencyCode: packageCurrencyToCurrencyCode(pkg.pricing ? pkg.pricing.currency : null),
    currentRevenue: convertPackagePricingToSalesOrderRevenue(pkg.pricing),
    quantity: 1,
  });
}

export function OpportunityToOrderLineItems(lineItems: OpportunityLineItem[]): LineItem[] {
  if (!lineItems) return [];
  return lineItems.map((li) => new LineItem({ ...li })) ?? [];
}

export function OrderToOpportunityLineItems(lineItems: LineItem[]): OpportunityLineItem[] {
  if (!lineItems) return [];
  return lineItems.map((li) => new OpportunityLineItem({ ...li }));
}

export function appToSalesOrderLineItem(app: App, currency: string): LineItem {
  return new LineItem({
    appKey: app.key,
    currencyCode: currency,
    currentRevenue: new Revenue({
      revenueComponents: [
        new RevenueComponent({
          value: 0,
          period: null, // Period is not set here to force the user to interact with this price before saving
          isStartingRevenue: false,
        }),
      ],
    }),
    quantity: 1,
  });
}

export function convertMarketplaceFreqToRevenueFreq(freq: UnitBillingFrequency): RevenuePeriod {
  if (freq === UnitBillingFrequency.UNIT_BILLING_FREQUENCY_MONTHLY) {
    return RevenuePeriod.MONTHLY;
  } else if (freq === UnitBillingFrequency.UNIT_BILLING_FREQUENCY_ONETIME) {
    return RevenuePeriod.ONETIME;
  } else if (freq === UnitBillingFrequency.UNIT_BILLING_FREQUENCY_YEARLY) {
    return RevenuePeriod.YEARLY;
  }
  return RevenuePeriod.MONTHLY;
}

export function convertAppPriceToRevenuePrice(appPrice: AppPrice, currencyCode: string, editionID: string): Revenue {
  const frequencyPrice =
    appPrice?.pricesForEditions?.[editionID]?.pricesForCurrencies?.[currencyCode]?.pricesForFrequencies?.[0];
  const pricingRules = frequencyPrice?.pricingRules?.[0];
  if (currencyCode === '' || !pricingRules) {
    return new Revenue({
      revenueComponents: [
        new RevenueComponent({
          value: 0,
        }),
      ],
    });
  }

  const revenueComponents = [
    new RevenueComponent({
      value: pricingRules.price,
      period: convertMarketplaceFreqToRevenueFreq(frequencyPrice.frequency),
      isStartingRevenue: false,
    }),
  ];

  if (frequencyPrice.setupFee > 0) {
    revenueComponents.push(
      new RevenueComponent({
        value: frequencyPrice.setupFee,
        period: RevenuePeriod.ONETIME,
        isStartingRevenue: false,
      }),
    );
  }

  return new Revenue({ revenueComponents });
}
